import { useEffect, useState } from "react";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable } from "../../components/common/DataTable";
import { FormModal } from "../../components/common/FormModal";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { StatusBadge } from "../../components/common/StatusBadge";
import { TextField, SelectField } from "../../components/common/Form";
import { Button } from "@/components/ui/button";
import {
  StockService,
  BottleProductsService,
  RestaurantStockService,
  BiteProductsService,
} from "../../services";
import { currency, dateOnly, todayISO } from "../../utils/format";
import { getBarProductTypeLabel } from "../../utils/barProducts";
import { Plus, Pencil, Trash2, Layers, X, GitBranch } from "lucide-react";
import { toast } from "sonner";
import { useCan } from "../../hooks/useCan";
import { SearchableSelectField } from "../common/SearchableSelectField";
import { BatchJourneyDialog } from "./BatchJourneyDialog";

const STOCK_TABS = {
  BOTTLE: "BOTTLE",
  RESTAURANT: "RESTAURANT",
};

const empty = {
  bottleId: "",
  purchaseTypeCode: "D",
  purchaseQuantity: 0,
  totalPrice: 0,
  costPrice: 0,
  purchaseDate: todayISO(),
  invoiceNumber: "",
  sourceReference: "",
  note: "",
};

const emptyBulk = {
  purchaseTypeCode: "D",
  purchaseDate: todayISO(),
  invoiceNumber: "",
  sourceReference: "",
  note: "",
  items: [
    {
      bottleId: "",
      purchaseQuantity: 0,
      totalPrice: 0,
      costPrice: 0,
    },
  ],
};

const emptyRestaurant = {
  biteProductId: "",
  quantityPurchased: 0,
  totalPrice: 0,
  unitCost: 0,
  purchaseDate: todayISO(),
  supplierName: "",
  invoiceNo: "",
  expiryDate: "",
  note: "",
};

const emptyRestaurantBulk = {
  purchaseDate: todayISO(),
  supplierName: "",
  invoiceNo: "",
  note: "",
  items: [
    {
      biteProductId: "",
      quantityPurchased: 0,
      totalPrice: 0,
      unitCost: 0,
      expiryDate: "",
    },
  ],
};

const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100;

const calculateUnitCost = (totalPrice, quantity) => {
  const total = Number(totalPrice || 0);
  const qty = Number(quantity || 0);

  if (qty <= 0) {
    return 0;
  }

  return roundMoney(total / qty);
};

const calculateTotalPrice = (unitCost, quantity) => {
  return roundMoney(Number(unitCost || 0) * Number(quantity || 0));
};

const OUTSIDE_INVOICE_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const buildOutsideStockInvoiceNumber = (purchaseDate) => {
  if (!purchaseDate) {
    return "";
  }

  const [year, month, day] = String(purchaseDate).split("-");

  if (!year || !month || !day) {
    return "";
  }

  const monthLabel = OUTSIDE_INVOICE_MONTHS[Number(month) - 1];

  if (!monthLabel) {
    return "";
  }

  return `INV_${day}_${monthLabel}_${year}`;
};

const getInvoiceNumberAfterPurchaseTypeChange = (
  previousForm,
  nextPurchaseTypeCode
) => {
  const generatedInvoiceNumber = buildOutsideStockInvoiceNumber(
    previousForm.purchaseDate
  );

  if (nextPurchaseTypeCode === "O") {
    return generatedInvoiceNumber;
  }

  if (
    previousForm.purchaseTypeCode === "O" &&
    previousForm.invoiceNumber === generatedInvoiceNumber
  ) {
    return "";
  }

  return previousForm.invoiceNumber;
};

const getInvoiceNumberAfterPurchaseDateChange = (
  previousForm,
  nextPurchaseDate
) => {
  if (previousForm.purchaseTypeCode === "O") {
    return buildOutsideStockInvoiceNumber(nextPurchaseDate);
  }

  return previousForm.invoiceNumber;
};

export const StockBatchesPage = () => {
  const can = useCan();

  const [activeTab, setActiveTab] = useState(STOCK_TABS.BOTTLE);

  const [rows, setRows] = useState([]);
  const [bottles, setBottles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [restaurantRows, setRestaurantRows] = useState([]);
  const [restaurantItems, setRestaurantItems] = useState([]);
  const [restaurantLoading, setRestaurantLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState(emptyBulk);
  const [bulkErrors, setBulkErrors] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);

  const [restaurantOpen, setRestaurantOpen] = useState(false);
  const [restaurantEditingId, setRestaurantEditingId] = useState(null);
  const [restaurantForm, setRestaurantForm] = useState(emptyRestaurant);
  const [restaurantErrors, setRestaurantErrors] = useState({});
  const [restaurantSaving, setRestaurantSaving] = useState(false);

  const [restaurantBulkOpen, setRestaurantBulkOpen] = useState(false);
  const [restaurantBulkForm, setRestaurantBulkForm] =
    useState(emptyRestaurantBulk);
  const [restaurantBulkErrors, setRestaurantBulkErrors] = useState({});
  const [restaurantBulkSaving, setRestaurantBulkSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmRestaurantDelete, setConfirmRestaurantDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  const [restaurantBusy, setRestaurantBusy] = useState(false);
  const [journeyBatch, setJourneyBatch] = useState(null);

  const load = () => {
    setLoading(true);

    Promise.all([StockService.list(), BottleProductsService.activeForStockEntry()])
      .then(([s, b]) => {
        setRows(s);
        setBottles(b);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message || "Failed to load bottle stock");
      })
      .finally(() => setLoading(false));
  };

  const loadRestaurant = () => {
    setRestaurantLoading(true);

    Promise.all([RestaurantStockService.list(), BiteProductsService.activeStockTrackedForStockEntry()])
      .then(([s, b]) => {
        setRestaurantRows(s);
        setRestaurantItems(b);
      })
      .catch((e) => {
        toast.error(
          e?.response?.data?.message || "Failed to load restaurant stock"
        );
      })
      .finally(() => setRestaurantLoading(false));
  };

  useEffect(() => {
    load();
    loadRestaurant();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...empty, purchaseDate: todayISO() });
    setErrors({});
    setOpen(true);
  };

  const openBulkCreate = () => {
    setBulkForm({
      ...emptyBulk,
      purchaseDate: todayISO(),
      items: [
        {
          bottleId: "",
          purchaseQuantity: 0,
          totalPrice: 0,
          costPrice: 0,
        },
      ],
    });
    setBulkErrors({});
    setBulkOpen(true);
  };

  const openEdit = (r) => {
    const purchaseTypeCode = r.purchaseTypeCode || "D";
    const purchaseDate = r.purchaseDate || todayISO();

    setEditingId(r.batchId);
    setForm({
      bottleId: r.bottleId ?? "",
      purchaseTypeCode,
      purchaseQuantity: r.purchaseQuantity ?? 0,
      totalPrice: calculateTotalPrice(r.costPrice, r.purchaseQuantity),
      costPrice: r.costPrice ?? 0,
      purchaseDate,
      invoiceNumber:
        purchaseTypeCode === "O"
          ? buildOutsideStockInvoiceNumber(purchaseDate)
          : r.invoiceNumber || "",
      sourceReference: r.sourceReference || "",
      note: r.note || "",
    });
    setErrors({});
    setOpen(true);
  };

  const openRestaurantCreate = () => {
    setRestaurantEditingId(null);
    setRestaurantForm({ ...emptyRestaurant, purchaseDate: todayISO() });
    setRestaurantErrors({});
    setRestaurantOpen(true);
  };

  const openRestaurantBulkCreate = () => {
    setRestaurantBulkForm({
      ...emptyRestaurantBulk,
      purchaseDate: todayISO(),
      items: [
        {
          biteProductId: "",
          quantityPurchased: 0,
          totalPrice: 0,
          unitCost: 0,
          expiryDate: "",
        },
      ],
    });
    setRestaurantBulkErrors({});
    setRestaurantBulkOpen(true);
  };

  const openRestaurantEdit = (r) => {
    setRestaurantEditingId(r.batchId);
    setRestaurantForm({
      biteProductId: r.biteProductId ?? "",
      quantityPurchased: r.quantityPurchased ?? 0,
      totalPrice: calculateTotalPrice(r.unitCost, r.quantityPurchased),
      unitCost: r.unitCost ?? 0,
      purchaseDate: r.purchaseDate || todayISO(),
      supplierName: r.supplierName || "",
      invoiceNo: r.invoiceNo || "",
      expiryDate: r.expiryDate || "",
      note: r.note || "",
    });
    setRestaurantErrors({});
    setRestaurantOpen(true);
  };

  const validate = () => {
    const e = {};

    if (!form.bottleId) e.bottleId = "Required";
    if (!form.purchaseTypeCode) e.purchaseTypeCode = "Required";

    if (!form.purchaseQuantity || Number(form.purchaseQuantity) <= 0) {
      e.purchaseQuantity = "Must be > 0";
    }

    if (form.totalPrice == null || Number(form.totalPrice) < 0) {
      e.totalPrice = "Must be ≥ 0";
    }

    if (!form.purchaseDate) e.purchaseDate = "Required";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateRestaurant = () => {
    const e = {};

    if (!restaurantForm.biteProductId) {
      e.biteProductId = "Required";
    }

    if (
      !restaurantForm.quantityPurchased ||
      Number(restaurantForm.quantityPurchased) <= 0
    ) {
      e.quantityPurchased = "Must be > 0";
    }

    if (
      restaurantForm.totalPrice == null ||
      Number(restaurantForm.totalPrice) < 0
    ) {
      e.totalPrice = "Must be ≥ 0";
    }

    if (!restaurantForm.purchaseDate) {
      e.purchaseDate = "Required";
    }

    setRestaurantErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateBulk = () => {
    const e = {};

    if (!bulkForm.purchaseTypeCode) {
      e.purchaseTypeCode = "Required";
    }

    if (!bulkForm.purchaseDate) {
      e.purchaseDate = "Required";
    }

    if (!bulkForm.items || bulkForm.items.length === 0) {
      e.items = "At least one bottle item is required";
    }

    const itemErrors = [];

    bulkForm.items.forEach((item, index) => {
      const rowError = {};

      if (!item.bottleId) {
        rowError.bottleId = "Required";
      }

      if (!item.purchaseQuantity || Number(item.purchaseQuantity) <= 0) {
        rowError.purchaseQuantity = "Must be > 0";
      }

      if (item.totalPrice == null || Number(item.totalPrice) < 0) {
        rowError.totalPrice = "Must be ≥ 0";
      }

      if (Object.keys(rowError).length > 0) {
        itemErrors[index] = rowError;
      }
    });

    const selectedBottleIds = bulkForm.items
      .map((item) => String(item.bottleId || ""))
      .filter(Boolean);

    const hasDuplicateBottle =
      new Set(selectedBottleIds).size !== selectedBottleIds.length;

    if (hasDuplicateBottle) {
      e.items = "Same bottle cannot be added twice in one bulk purchase";
    }

    if (itemErrors.length > 0) {
      e.itemErrors = itemErrors;
    }

    setBulkErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateRestaurantBulk = () => {
    const e = {};

    if (!restaurantBulkForm.purchaseDate) {
      e.purchaseDate = "Required";
    }

    if (!restaurantBulkForm.items || restaurantBulkForm.items.length === 0) {
      e.items = "At least one restaurant item is required";
    }

    const itemErrors = [];

    restaurantBulkForm.items.forEach((item, index) => {
      const rowError = {};

      if (!item.biteProductId) {
        rowError.biteProductId = "Required";
      }

      if (!item.quantityPurchased || Number(item.quantityPurchased) <= 0) {
        rowError.quantityPurchased = "Must be > 0";
      }

      if (item.totalPrice == null || Number(item.totalPrice) < 0) {
        rowError.totalPrice = "Must be ≥ 0";
      }

      if (Object.keys(rowError).length > 0) {
        itemErrors[index] = rowError;
      }
    });

    const selectedItemIds = restaurantBulkForm.items
      .map((item) => String(item.biteProductId || ""))
      .filter(Boolean);

    const hasDuplicateItem =
      new Set(selectedItemIds).size !== selectedItemIds.length;

    if (hasDuplicateItem) {
      e.items = "Same restaurant item cannot be added twice in one bulk purchase";
    }

    if (itemErrors.length > 0) {
      e.itemErrors = itemErrors;
    }

    setRestaurantBulkErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;

    setSaving(true);

    const payload = {
      bottleId: Number(form.bottleId),
      purchaseTypeCode: form.purchaseTypeCode,
      purchaseQuantity: Number(form.purchaseQuantity),
      costPrice: calculateUnitCost(form.totalPrice, form.purchaseQuantity),
      purchaseDate: form.purchaseDate,
      invoiceNumber:
        form.purchaseTypeCode === "O"
          ? buildOutsideStockInvoiceNumber(form.purchaseDate) || null
          : form.invoiceNumber || null,
      sourceReference: form.sourceReference || null,
      note: form.note || null,
    };

    try {
      if (editingId) {
        await StockService.update(editingId, payload);
        toast.success("Stock batch updated");
      } else {
        await StockService.create(payload);
        toast.success("Stock batch added");
      }

      setOpen(false);
      setForm(empty);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const submitRestaurant = async () => {
    if (!validateRestaurant()) return;

    setRestaurantSaving(true);

    const payload = {
      biteProductId: Number(restaurantForm.biteProductId),
      quantityPurchased: Number(restaurantForm.quantityPurchased),
      unitCost: calculateUnitCost(
        restaurantForm.totalPrice,
        restaurantForm.quantityPurchased
      ),
      purchaseDate: restaurantForm.purchaseDate,
      supplierName: restaurantForm.supplierName || null,
      invoiceNo: restaurantForm.invoiceNo || null,
      expiryDate: restaurantForm.expiryDate || null,
      note: restaurantForm.note || null,
    };

    try {
      if (restaurantEditingId) {
        await RestaurantStockService.update(restaurantEditingId, payload);
        toast.success("Restaurant stock batch updated");
      } else {
        await RestaurantStockService.create(payload);
        toast.success("Restaurant stock batch added");
      }

      setRestaurantOpen(false);
      setRestaurantForm(emptyRestaurant);
      setRestaurantEditingId(null);
      loadRestaurant();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed");
    } finally {
      setRestaurantSaving(false);
    }
  };

  const submitBulk = async () => {
    if (!validateBulk()) return;

    setBulkSaving(true);

    const payload = {
      purchaseTypeCode: bulkForm.purchaseTypeCode,
      purchaseDate: bulkForm.purchaseDate,
      invoiceNumber:
        bulkForm.purchaseTypeCode === "O"
          ? buildOutsideStockInvoiceNumber(bulkForm.purchaseDate) || null
          : bulkForm.invoiceNumber || null,
      sourceReference: bulkForm.sourceReference || null,
      note: bulkForm.note || null,
      items: bulkForm.items.map((item) => ({
        bottleId: Number(item.bottleId),
        purchaseQuantity: Number(item.purchaseQuantity),
        costPrice: calculateUnitCost(item.totalPrice, item.purchaseQuantity),
      })),
    };

    try {
      await StockService.bulkCreate(payload);

      toast.success("Bulk stock purchase added");
      setBulkOpen(false);
      setBulkForm(emptyBulk);
      setBulkErrors({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed");
    } finally {
      setBulkSaving(false);
    }
  };

  const submitRestaurantBulk = async () => {
    if (!validateRestaurantBulk()) return;

    setRestaurantBulkSaving(true);

    const payload = {
      purchaseDate: restaurantBulkForm.purchaseDate,
      supplierName: restaurantBulkForm.supplierName || null,
      invoiceNo: restaurantBulkForm.invoiceNo || null,
      note: restaurantBulkForm.note || null,
      items: restaurantBulkForm.items.map((item) => ({
        biteProductId: Number(item.biteProductId),
        quantityPurchased: Number(item.quantityPurchased),
        unitCost: calculateUnitCost(item.totalPrice, item.quantityPurchased),
        expiryDate: item.expiryDate || null,
      })),
    };

    try {
      await RestaurantStockService.bulkCreate(payload);

      toast.success("Bulk restaurant stock purchase added");
      setRestaurantBulkOpen(false);
      setRestaurantBulkForm(emptyRestaurantBulk);
      setRestaurantBulkErrors({});
      loadRestaurant();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed");
    } finally {
      setRestaurantBulkSaving(false);
    }
  };

  const remove = async () => {
    setBusy(true);

    try {
      await StockService.remove(confirmDelete.batchId);
      toast.success("Batch deactivated");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const removeRestaurant = async () => {
    setRestaurantBusy(true);

    try {
      await RestaurantStockService.remove(confirmRestaurantDelete.batchId);
      toast.success("Restaurant stock batch deactivated");
      setConfirmRestaurantDelete(null);
      loadRestaurant();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed");
    } finally {
      setRestaurantBusy(false);
    }
  };

  const updateBulkField = (field, value) => {
    setBulkForm((previousForm) => {
      const updatedForm = {
        ...previousForm,
        [field]: value,
      };

      if (field === "purchaseTypeCode") {
        updatedForm.invoiceNumber = getInvoiceNumberAfterPurchaseTypeChange(
          previousForm,
          value
        );
      }

      if (field === "purchaseDate") {
        updatedForm.invoiceNumber = getInvoiceNumberAfterPurchaseDateChange(
          previousForm,
          value
        );
      }

      return updatedForm;
    });
  };

  const updateRestaurantBulkField = (field, value) => {
    setRestaurantBulkForm({
      ...restaurantBulkForm,
      [field]: value,
    });
  };

  const updateBulkItem = (index, field, value) => {
    setBulkForm({
      ...bulkForm,
      items: bulkForm.items.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        const updatedItem = {
          ...item,
          [field]: value,
        };

        if (field === "purchaseQuantity" || field === "totalPrice") {
          updatedItem.costPrice = calculateUnitCost(
            updatedItem.totalPrice,
            updatedItem.purchaseQuantity
          );
        }

        return updatedItem;
      }),
    });
  };

  const updateRestaurantBulkItem = (index, field, value) => {
    setRestaurantBulkForm({
      ...restaurantBulkForm,
      items: restaurantBulkForm.items.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        const updatedItem = {
          ...item,
          [field]: value,
        };

        if (field === "quantityPurchased" || field === "totalPrice") {
          updatedItem.unitCost = calculateUnitCost(
            updatedItem.totalPrice,
            updatedItem.quantityPurchased
          );
        }

        return updatedItem;
      }),
    });
  };

  const addBulkItem = () => {
    setBulkForm({
      ...bulkForm,
      items: [
        ...bulkForm.items,
        {
          bottleId: "",
          purchaseQuantity: 0,
          totalPrice: 0,
          costPrice: 0,
        },
      ],
    });
  };

  const addRestaurantBulkItem = () => {
    setRestaurantBulkForm({
      ...restaurantBulkForm,
      items: [
        ...restaurantBulkForm.items,
        {
          biteProductId: "",
          quantityPurchased: 0,
          totalPrice: 0,
          unitCost: 0,
          expiryDate: "",
        },
      ],
    });
  };

  const removeBulkItem = (index) => {
    if (bulkForm.items.length === 1) {
      toast.error("At least one item is required");
      return;
    }

    setBulkForm({
      ...bulkForm,
      items: bulkForm.items.filter((_, itemIndex) => itemIndex !== index),
    });
  };

  const removeRestaurantBulkItem = (index) => {
    if (restaurantBulkForm.items.length === 1) {
      toast.error("At least one item is required");
      return;
    }

    setRestaurantBulkForm({
      ...restaurantBulkForm,
      items: restaurantBulkForm.items.filter(
        (_, itemIndex) => itemIndex !== index
      ),
    });
  };

  const bottleOptions = bottles.map((b) => ({
    value: b.bottleId,
    label: b.barProductType === "CIGARETTE"
      ? `${b.bottleName} (Cigarette unit)`
      : `${b.bottleName} (${b.bottleSize}) - ${b.volumeMl} ML`,
  }));

  const restaurantItemOptions = restaurantItems.map((item) => ({
      value: item.biteId,
      label: `${item.biteName} (${item.itemSection || "FOOD"})`,
    }));

  const actionButtonClass = "w-full sm:w-auto";
  const tableWrapperClass = "w-full max-w-full overflow-x-auto rounded-xl";
  const modalGridClass = "grid grid-cols-1 sm:grid-cols-2 gap-3";
  const modalScrollClass = "max-h-[72vh] overflow-y-auto pr-2 pb-2";
  const bulkRowsScrollClass =
    "max-h-[44vh] overflow-y-auto divide-y divide-slate-100";

  return (
    <div className="w-full max-w-full overflow-hidden">
      <PageHeader
        title="Stock Batches"
        subtitle={
          activeTab === STOCK_TABS.BOTTLE
            ? "BAR product stock with cost price tracked per batch. Cashier never sees this data."
            : "Restaurant item stock for food, beverages and goods with cost tracked per batch."
        }
        actions={
          can.create && (
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end">
              {activeTab === STOCK_TABS.BOTTLE ? (
                <>
                  <Button
                    onClick={openBulkCreate}
                    className={`bg-amber-500 hover:bg-amber-600 text-navy-900 ${actionButtonClass}`}
                    data-testid="stock-bulk-add"
                  >
                    <Layers className="h-4 w-4 mr-2" />
                    Bulk Purchase
                  </Button>

                  <Button
                    onClick={openCreate}
                    className={`bg-navy-800 hover:bg-navy-900 text-white ${actionButtonClass}`}
                    data-testid="stock-add"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Batch
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={openRestaurantBulkCreate}
                    className={`bg-amber-500 hover:bg-amber-600 text-navy-900 ${actionButtonClass}`}
                    data-testid="restaurant-stock-bulk-add"
                  >
                    <Layers className="h-4 w-4 mr-2" />
                    Bulk Restaurant Stock
                  </Button>

                  <Button
                    onClick={openRestaurantCreate}
                    className={`bg-navy-800 hover:bg-navy-900 text-white ${actionButtonClass}`}
                    data-testid="restaurant-stock-add"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Restaurant Stock
                  </Button>
                </>
              )}
            </div>
          )
        }
      />

      <div className="mb-4 grid grid-cols-1 gap-2 sm:inline-flex sm:w-auto sm:grid-cols-none sm:flex-row">
        <Button
          type="button"
          variant={activeTab === STOCK_TABS.BOTTLE ? "default" : "outline"}
          onClick={() => setActiveTab(STOCK_TABS.BOTTLE)}
          className={
            activeTab === STOCK_TABS.BOTTLE
              ? "w-full sm:w-auto bg-navy-800 hover:bg-navy-900 text-white"
              : "w-full sm:w-auto"
          }
        >
            BAR Stock
        </Button>

        <Button
          type="button"
          variant={activeTab === STOCK_TABS.RESTAURANT ? "default" : "outline"}
          onClick={() => setActiveTab(STOCK_TABS.RESTAURANT)}
          className={
            activeTab === STOCK_TABS.RESTAURANT
              ? "w-full sm:w-auto bg-navy-800 hover:bg-navy-900 text-white"
              : "w-full sm:w-auto"
          }
        >
          Restaurant Stock
        </Button>
      </div>

      {activeTab === STOCK_TABS.BOTTLE && (
        <div className={tableWrapperClass}>
          <DataTable
            loading={loading}
            rows={rows.map((r) => ({ ...r, key: r.batchId }))}
            testId="stock-table"
            searchable
            searchKeys={[
              "batchCode",
              "bottleName",
              "purchaseTypeCode",
              "invoiceNumber",
              "addedByFullName",
              "status",
            ]}
            searchPlaceholder="Search batches by code, product or added-by…"
            columns={[
              { key: "batchCode", header: "Batch" },
              {
                key: "invoiceNumber",
                header: "Invoice",
                render: (r) => r.invoiceNumber || "—",
              },
              { key: "bottleName", header: "Product" },
              {
                key: "barProductType",
                header: "Product Type",
                render: (r) => getBarProductTypeLabel(r),
              },
              {
                key: "volumeMl",
                header: "Volume / Unit",
                render: (r) => r.barProductType === "CIGARETTE" ? "Units" : (r.volumeMl ? `${r.volumeMl} ML` : "—"),
              },
              {
                key: "purchaseTypeCode",
                header: "Code",
                render: (r) => <StatusBadge value={r.purchaseTypeCode} />,
              },
              { key: "purchaseQuantity", header: "Purchased", render: (r) => `${r.purchaseQuantity} ${r.barProductType === "CIGARETTE" ? "units" : "bottles"}` },
              { key: "remainingQuantity", header: "Remaining", render: (r) => `${r.remainingQuantity} ${r.barProductType === "CIGARETTE" ? "units" : "bottles"}` },
              {
                key: "costPrice",
                header: "Cost",
                render: (r) => currency(r.costPrice),
              },
              {
                key: "sellingPrice",
                header: "Selling",
                render: (r) => currency(r.sellingPrice),
              },
              {
                key: "purchaseDate",
                header: "Purchased on",
                render: (r) => dateOnly(r.purchaseDate),
              },
              { key: "addedByFullName", header: "Added by" },
              {
                key: "status",
                header: "Status",
                render: (r) => <StatusBadge value={r.status} />,
              },
              {
                key: "actions",
                header: "",
                render: (r) =>
                  can.modify ? (
                    <div className="flex flex-wrap gap-2">
                      {can.isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setJourneyBatch({ source: "BOTTLE", batchId: r.batchId })}
                          title="View Journey"
                          aria-label={`View journey for ${r.bottleName} batch ${r.batchCode}`}
                        >
                          <GitBranch className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEdit(r)}
                        data-testid={`stock-edit-${r.batchId}`}
                        title="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmDelete(r)}
                        data-testid={`stock-delete-${r.batchId}`}
                        title="Deactivate batch"
                        className="text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <span className="text-slate-300 text-xs">—</span>
                  ),
              },
            ]}
          />
        </div>
      )}

      {activeTab === STOCK_TABS.RESTAURANT && (
        <div className={tableWrapperClass}>
          <DataTable
            loading={restaurantLoading}
            rows={restaurantRows.map((r) => ({ ...r, key: r.batchId }))}
            testId="restaurant-stock-table"
            searchable
            searchKeys={[
              "biteName",
              "itemSection",
              "biteCategoryName",
              "supplierName",
              "invoiceNo",
              "createdByFullName",
              "status",
            ]}
            searchPlaceholder="Search restaurant stock by item, section, supplier or invoice…"
            columns={[
              { key: "biteName", header: "Item" },
              {
                key: "itemSection",
                header: "Section",
                render: (r) => <StatusBadge value={r.itemSection || "FOOD"} />,
              },
              { key: "biteCategoryName", header: "Category" },
              {
                key: "supplierName",
                header: "Supplier",
                render: (r) => r.supplierName || "—",
              },
              {
                key: "invoiceNo",
                header: "Invoice",
                render: (r) => r.invoiceNo || "—",
              },
              { key: "quantityPurchased", header: "Purchased" },
              { key: "quantityAvailable", header: "Available" },
              {
                key: "unitCost",
                header: "Unit Cost",
                render: (r) => currency(r.unitCost),
              },
              {
                key: "totalCost",
                header: "Total Cost",
                render: (r) => currency(r.totalCost),
              },
              {
                key: "expiryDate",
                header: "Expiry",
                render: (r) => (r.expiryDate ? dateOnly(r.expiryDate) : "—"),
              },
              {
                key: "purchaseDate",
                header: "Purchased on",
                render: (r) => dateOnly(r.purchaseDate),
              },
              {
                key: "createdByFullName",
                header: "Added by",
                render: (r) =>
                  r.createdByFullName || r.createdByUsername || "—",
              },
              {
                key: "status",
                header: "Status",
                render: (r) => <StatusBadge value={r.status} />,
              },
              {
                key: "actions",
                header: "",
                render: (r) =>
                  can.modify ? (
                    <div className="flex flex-wrap gap-2">
                      {can.isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setJourneyBatch({ source: "RESTAURANT", batchId: r.batchId })}
                          title="View Journey"
                          aria-label={`View journey for ${r.biteName} batch ${r.batchId}`}
                        >
                          <GitBranch className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openRestaurantEdit(r)}
                        data-testid={`restaurant-stock-edit-${r.batchId}`}
                        title="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmRestaurantDelete(r)}
                        data-testid={`restaurant-stock-delete-${r.batchId}`}
                        title="Deactivate restaurant stock batch"
                        className="text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <span className="text-slate-300 text-xs">—</span>
                  ),
              },
            ]}
          />
        </div>
      )}

      <FormModal
        open={open}
        onOpenChange={setOpen}
        title={editingId ? "Edit stock batch" : "New stock batch"}
        onSubmit={submit}
        loading={saving}
        size="lg"
        submitText={editingId ? "Save" : "Create"}
        description={
          editingId
            ? "Editing a batch keeps already-consumed quantity intact."
            : "Add a new stock purchase batch."
        }
      >
        <div className={modalScrollClass}>
          <div className={modalGridClass}>
            <SearchableSelectField
              label="BAR Product"
              required
              value={form.bottleId}
              onChange={(v) => setForm({ ...form, bottleId: v })}
              options={bottleOptions}
              placeholder="Select product"
              searchPlaceholder="Search by product or product details..."
              error={errors.bottleId}
              testId="stock-bottle"
            />

            <SelectField
              label="Purchase code"
              required
              value={form.purchaseTypeCode}
              onChange={(v) =>
                setForm((previousForm) => ({
                  ...previousForm,
                  purchaseTypeCode: v,
                  invoiceNumber: getInvoiceNumberAfterPurchaseTypeChange(
                    previousForm,
                    v
                  ),
                }))
              }
              options={[
                { value: "D", label: "D" },
                { value: "O", label: "O" },
              ]}
              error={errors.purchaseTypeCode}
              testId="stock-code"
            />

            <TextField
              label="Purchase quantity"
              required
              type="number"
              value={form.purchaseQuantity}
              onChange={(v) =>
                setForm({
                  ...form,
                  purchaseQuantity: v,
                  costPrice: calculateUnitCost(form.totalPrice, v),
                })
              }
              error={errors.purchaseQuantity}
            />

            <TextField
              label="Total purchase price"
              required
              type="number"
              value={form.totalPrice}
              onChange={(v) =>
                setForm({
                  ...form,
                  totalPrice: v,
                  costPrice: calculateUnitCost(v, form.purchaseQuantity),
                })
              }
              error={errors.totalPrice}
            />

            <CalculatedCostField
              label="Unit cost"
              value={calculateUnitCost(form.totalPrice, form.purchaseQuantity)}
            />

            <TextField
              label="Purchase date"
              required
              type="date"
              value={form.purchaseDate}
              onChange={(v) =>
                setForm((previousForm) => ({
                  ...previousForm,
                  purchaseDate: v,
                  invoiceNumber: getInvoiceNumberAfterPurchaseDateChange(
                    previousForm,
                    v
                  ),
                }))
              }
              error={errors.purchaseDate}
            />

            <TextField
              label="Invoice number"
              value={form.invoiceNumber}
              disabled={form.purchaseTypeCode === "O"}
              onChange={(v) => setForm({ ...form, invoiceNumber: v })}
            />

            <TextField
              label="Source reference"
              value={form.sourceReference}
              onChange={(v) => setForm({ ...form, sourceReference: v })}
            />
          </div>

          <div className="mt-3">
            <TextField
              label="Note"
              value={form.note}
              onChange={(v) => setForm({ ...form, note: v })}
            />
          </div>
        </div>
      </FormModal>

      <FormModal
        open={restaurantOpen}
        onOpenChange={setRestaurantOpen}
        title={
          restaurantEditingId
            ? "Edit restaurant stock batch"
            : "New restaurant stock batch"
        }
        onSubmit={submitRestaurant}
        loading={restaurantSaving}
        size="lg"
        submitText={restaurantEditingId ? "Save" : "Create"}
        description={
          restaurantEditingId
            ? "Editing a restaurant stock batch keeps already-consumed quantity intact."
            : "Add a new stock batch for a stock-tracked food, beverage or goods item."
        }
      >
        <div className={modalScrollClass}>
          <div className={modalGridClass}>
            <SearchableSelectField
              label="Restaurant item"
              required
              value={restaurantForm.biteProductId}
              onChange={(v) =>
                setRestaurantForm({ ...restaurantForm, biteProductId: v })
              }
              options={restaurantItemOptions}
              placeholder="Select stock-tracked item"
              searchPlaceholder="Search food, beverage or goods..."
              error={restaurantErrors.biteProductId}
              testId="restaurant-stock-item"
            />

            <TextField
              label="Purchase quantity"
              required
              type="number"
              value={restaurantForm.quantityPurchased}
              onChange={(v) =>
                setRestaurantForm({
                  ...restaurantForm,
                  quantityPurchased: v,
                  unitCost: calculateUnitCost(restaurantForm.totalPrice, v),
                })
              }
              error={restaurantErrors.quantityPurchased}
            />

            <TextField
              label="Total purchase price"
              required
              type="number"
              value={restaurantForm.totalPrice}
              onChange={(v) =>
                setRestaurantForm({
                  ...restaurantForm,
                  totalPrice: v,
                  unitCost: calculateUnitCost(
                    v,
                    restaurantForm.quantityPurchased
                  ),
                })
              }
              error={restaurantErrors.totalPrice}
            />

            <CalculatedCostField
              label="Unit cost"
              value={calculateUnitCost(
                restaurantForm.totalPrice,
                restaurantForm.quantityPurchased
              )}
            />

            <TextField
              label="Purchase date"
              required
              type="date"
              value={restaurantForm.purchaseDate}
              onChange={(v) =>
                setRestaurantForm({
                  ...restaurantForm,
                  purchaseDate: v,
                })
              }
              error={restaurantErrors.purchaseDate}
            />

            <TextField
              label="Supplier name"
              value={restaurantForm.supplierName}
              onChange={(v) =>
                setRestaurantForm({
                  ...restaurantForm,
                  supplierName: v,
                })
              }
            />

            <TextField
              label="Invoice number"
              value={restaurantForm.invoiceNo}
              onChange={(v) =>
                setRestaurantForm({
                  ...restaurantForm,
                  invoiceNo: v,
                })
              }
            />

            <TextField
              label="Expiry date"
              type="date"
              value={restaurantForm.expiryDate}
              onChange={(v) =>
                setRestaurantForm({
                  ...restaurantForm,
                  expiryDate: v,
                })
              }
            />
          </div>

          <div className="mt-3">
            <TextField
              label="Note"
              value={restaurantForm.note}
              onChange={(v) =>
                setRestaurantForm({
                  ...restaurantForm,
                  note: v,
                })
              }
            />
          </div>
        </div>
      </FormModal>

      <FormModal
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        title="Bulk stock purchase"
        description="Add multiple bottle batches using one common invoice and purchase date."
        onSubmit={submitBulk}
        loading={bulkSaving}
        size="xl"
        submitText="Create Batches"
      >
        <div className={`${modalScrollClass} space-y-4`}>
          <div className={modalGridClass}>
            <SelectField
              label="Purchase code"
              required
              value={bulkForm.purchaseTypeCode}
              onChange={(v) => updateBulkField("purchaseTypeCode", v)}
              options={[
                { value: "D", label: "D" },
                { value: "O", label: "O" },
              ]}
              error={bulkErrors.purchaseTypeCode}
            />

            <TextField
              label="Purchase date"
              required
              type="date"
              value={bulkForm.purchaseDate}
              onChange={(v) => updateBulkField("purchaseDate", v)}
              error={bulkErrors.purchaseDate}
            />

            <TextField
              label="Invoice number"
              value={bulkForm.invoiceNumber}
              disabled={bulkForm.purchaseTypeCode === "O"}
              onChange={(v) => updateBulkField("invoiceNumber", v)}
            />

            <TextField
              label="Source reference"
              value={bulkForm.sourceReference}
              onChange={(v) => updateBulkField("sourceReference", v)}
            />
          </div>

          <div className="w-full overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="font-display text-sm font-semibold text-slate-900">
                  Bottle items
                </h3>

                <p className="text-xs text-slate-500">
                  Each row will create one normal stock batch.
                </p>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addBulkItem}
                className="w-full sm:w-auto"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Row
              </Button>
            </div>

            {bulkErrors.items && (
              <div className="px-4 py-2 text-sm text-rose-600 bg-rose-50 border-b border-rose-100">
                {bulkErrors.items}
              </div>
            )}

            <div className={bulkRowsScrollClass}>
              {bulkForm.items.map((item, index) => {
                const itemError = bulkErrors.itemErrors?.[index] || {};

                return (
                  <div key={index} className="p-4">
                    <div className="mb-1 flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeBulkItem(index)}
                        className="grid h-8 w-8 place-items-center rounded-full border border-rose-100 bg-white text-rose-600 hover:bg-rose-50"
                        title="Remove row"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:items-start">
                      <SearchableSelectField
                        label="BAR Product"
                        required
                        value={item.bottleId}
                        onChange={(v) => updateBulkItem(index, "bottleId", v)}
                        options={bottleOptions}
                        placeholder="Select product"
                        searchPlaceholder="Search product..."
                        error={itemError.bottleId}
                      />

                      <TextField
                        label="Quantity"
                        required
                        type="number"
                        value={item.purchaseQuantity}
                        onChange={(v) =>
                          updateBulkItem(index, "purchaseQuantity", v)
                        }
                        error={itemError.purchaseQuantity}
                      />

                      <TextField
                        label="Total price"
                        required
                        type="number"
                        value={item.totalPrice}
                        onChange={(v) => updateBulkItem(index, "totalPrice", v)}
                        error={itemError.totalPrice}
                      />

                      <CalculatedCostField
                        label="Unit cost"
                        value={calculateUnitCost(
                          item.totalPrice,
                          item.purchaseQuantity
                        )}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <TextField
            label="Note"
            value={bulkForm.note}
            onChange={(v) => updateBulkField("note", v)}
          />
        </div>
      </FormModal>

      <FormModal
        open={restaurantBulkOpen}
        onOpenChange={setRestaurantBulkOpen}
        title="Bulk restaurant stock purchase"
        description="Add multiple restaurant stock batches using one common supplier, invoice and purchase date."
        onSubmit={submitRestaurantBulk}
        loading={restaurantBulkSaving}
        size="xl"
        submitText="Create Restaurant Batches"
      >
        <div className={`${modalScrollClass} space-y-4`}>
          <div className={modalGridClass}>
            <TextField
              label="Purchase date"
              required
              type="date"
              value={restaurantBulkForm.purchaseDate}
              onChange={(v) => updateRestaurantBulkField("purchaseDate", v)}
              error={restaurantBulkErrors.purchaseDate}
            />

            <TextField
              label="Supplier name"
              value={restaurantBulkForm.supplierName}
              onChange={(v) => updateRestaurantBulkField("supplierName", v)}
            />

            <TextField
              label="Invoice number"
              value={restaurantBulkForm.invoiceNo}
              onChange={(v) => updateRestaurantBulkField("invoiceNo", v)}
            />
          </div>

          <div className="w-full overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="font-display text-sm font-semibold text-slate-900">
                  Restaurant items
                </h3>

                <p className="text-xs text-slate-500">
                  Each row will create one restaurant stock batch.
                </p>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addRestaurantBulkItem}
                className="w-full sm:w-auto"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Row
              </Button>
            </div>

            {restaurantBulkErrors.items && (
              <div className="px-4 py-2 text-sm text-rose-600 bg-rose-50 border-b border-rose-100">
                {restaurantBulkErrors.items}
              </div>
            )}

            <div className={bulkRowsScrollClass}>
              {restaurantBulkForm.items.map((item, index) => {
                const itemError =
                  restaurantBulkErrors.itemErrors?.[index] || {};

                return (
                  <div key={index} className="p-4">
                    <div className="mb-3\1 flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeRestaurantBulkItem(index)}
                        className="grid h-8 w-8 place-items-center rounded-full border border-rose-100 bg-white text-rose-600 hover:bg-rose-50"
                        title="Remove row"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:items-start">
                      <SearchableSelectField
                        label="Restaurant item"
                        required
                        value={item.biteProductId}
                        onChange={(v) =>
                          updateRestaurantBulkItem(index, "biteProductId", v)
                        }
                        options={restaurantItemOptions}
                        placeholder="Select item"
                        searchPlaceholder="Search item..."
                        error={itemError.biteProductId}
                      />

                      <TextField
                        label="Quantity"
                        required
                        type="number"
                        value={item.quantityPurchased}
                        onChange={(v) =>
                          updateRestaurantBulkItem(index, "quantityPurchased", v)
                        }
                        error={itemError.quantityPurchased}
                      />

                      <TextField
                        label="Total price"
                        required
                        type="number"
                        value={item.totalPrice}
                        onChange={(v) =>
                          updateRestaurantBulkItem(index, "totalPrice", v)
                        }
                        error={itemError.totalPrice}
                      />

                      <CalculatedCostField
                        label="Unit cost"
                        value={calculateUnitCost(
                          item.totalPrice,
                          item.quantityPurchased
                        )}
                      />

                      <TextField
                        label="Expiry"
                        type="date"
                        value={item.expiryDate}
                        onChange={(v) =>
                          updateRestaurantBulkItem(index, "expiryDate", v)
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <TextField
            label="Note"
            value={restaurantBulkForm.note}
            onChange={(v) => updateRestaurantBulkField("note", v)}
          />
        </div>
      </FormModal>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        title="Deactivate batch?"
        description={
          confirmDelete
            ? `Batch ${confirmDelete.batchCode} (${confirmDelete.bottleName}) will be marked INACTIVE. FIFO consumption will skip it; existing sale history stays intact.`
            : ""
        }
        confirmText="Deactivate"
        danger
        loading={busy}
        onConfirm={remove}
      />

      <ConfirmDialog
        open={!!confirmRestaurantDelete}
        onOpenChange={(v) => !v && setConfirmRestaurantDelete(null)}
        title="Deactivate restaurant stock batch?"
        description={
          confirmRestaurantDelete
            ? `${confirmRestaurantDelete.biteName} batch #${confirmRestaurantDelete.batchId} will be marked INACTIVE. FIFO restaurant stock consumption will skip it; existing sale history stays intact.`
            : ""
        }
        confirmText="Deactivate"
        danger
        loading={restaurantBusy}
        onConfirm={removeRestaurant}
      />

      <BatchJourneyDialog
        open={Boolean(journeyBatch)}
        onOpenChange={(isOpen) => !isOpen && setJourneyBatch(null)}
        source={journeyBatch?.source}
        batchId={journeyBatch?.batchId}
      />
    </div>
  );
};

const CalculatedCostField = ({ label, value }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-slate-600">
      {label}
      <span className="text-rose-500">*</span>
    </label>

    <input
      type="number"
      value={value}
      disabled
      readOnly
      className="h-10 w-full rounded-lg border border-slate-200 bg-slate-100 px-3 text-sm text-slate-700 cursor-not-allowed"
    />
  </div>
);
